import { todayIn, zonedParts } from "../../../shared/dates";
import type { UvHour } from "../../../shared/sky/types";
import { protectionWindow, uvCategory, uvPeak } from "../../../shared/sky/uv";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { EmptyState } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { useUv } from "./api";
import { NeedsLocation, SkyFoot, useFitLevel, useTimeFmt } from "./common";

type UvConfig = Record<string, never>;

/** "11 AM" / "13:00" for a local hour number. */
function hourLabel(h: number, h24: boolean): string {
  if (h24) return `${String(h % 24).padStart(2, "0")}:00`;
  const hr = h % 12 || 12;
  return `${hr} ${h % 24 < 12 ? "AM" : "PM"}`;
}

function Uv({ size, fullscreen, openFullscreen }: WidgetProps<UvConfig>) {
  const { settings, timeZone } = useHousehold();
  const now = useNow("minute");
  const fmt = useTimeFmt();
  const q = useUv();
  // Short cards drop the hourly bars, then the advice, then the footer.
  const [fitRef, fit] = useFitLevel<HTMLDivElement>(3, size);
  // The EPA forecast is issued once a day; it only goes "stale" if we lose it for hours.
  const state = dataStateOf(q, 6 * 3_600_000);
  if (!settings.location) return <NeedsLocation icon="umbrella" title="UV index needs a home location" detail="Set a general location (City, ST) to see today’s UV forecast." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="umbrella" title="UV forecast unavailable" detail={q.error?.message} />;
  if (data.status === "unsupported") return <EmptyState icon="umbrella" title="UV forecast unavailable here" detail={data.reason} />;

  const today = todayIn(timeZone, now);
  // Never present a previous day's forecast as today's.
  if (data.date !== today) {
    return (
      <div className="sky sky-uv">
        <WidgetHeader title="UV index" />
        <EmptyState icon="umbrella" title="Today’s UV forecast isn’t out yet" detail={`Last forecast was for ${data.date}.`} />
        <SkyFoot source={`EPA / NWS forecast for ${data.city}, ${data.state}`} stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />
      </div>
    );
  }

  const hourNow = Math.floor(zonedParts(now, timeZone).minutes / 60);
  const current = data.hours.find((h) => h.hour === hourNow) ?? null;
  const peak = uvPeak(data.hours);
  const after = data.hours.length > 0 && hourNow > data.hours[data.hours.length - 1]!.hour;
  const before = data.hours.length > 0 && hourNow < data.hours[0]!.hour;
  const shownValue = current?.uv ?? (after || before ? 0 : null);
  const cat = uvCategory(shownValue ?? 0);
  const peakCat = peak ? uvCategory(peak.uv) : null;
  const win = protectionWindow(data.hours);
  const compact = size.variant === "compact";

  return (
    <div className={`sky sky-uv ${compact ? "is-compact" : ""}`} ref={fitRef}>
      {!compact && <WidgetHeader title="UV index" onTitle={fullscreen ? undefined : openFullscreen} meta="Forecast" />}
      <div className="uv-main">
        <div className="uv-badge" style={{ ["--uv" as string]: cat.color }}>
          <span className="uv-num">{shownValue ?? "—"}</span>
        </div>
        <div className="uv-text">
          <strong>{after ? "Sun’s low — UV is done for today" : before ? "Not yet — UV starts later" : cat.label}</strong>
          {!compact && peak && peakCat && (
            <span className="muted">
              Peak {peak.uv} ({peakCat.label.toLowerCase()}) at {hourLabel(peak.hour, fmt.h24)}
            </span>
          )}
          {!compact && fit < 2 && <span className="uv-advice">{win && !after ? `Protect ${hourLabel(win.from, fmt.h24)} – ${hourLabel(win.to, fmt.h24)}. ${uvCategory(peak?.uv ?? 0).advice}` : "No sun protection needed today."}</span>}
        </div>
      </div>
      {!compact && fit < 1 && <UvBars hours={data.hours} hourNow={hourNow} h24={fmt.h24} />}
      {!compact && fit < 3 && <SkyFoot source={`EPA / NWS forecast for ${data.city}, ${data.state}`} stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

function UvBars({ hours, hourNow, h24 }: { hours: UvHour[]; hourNow: number; h24: boolean }) {
  const max = Math.max(6, ...hours.map((h) => h.uv));
  return (
    <ol className="uv-bars" aria-label="Hourly UV forecast">
      {hours.map((h) => {
        const c = uvCategory(h.uv);
        return (
          <li key={h.hour} className={h.hour === hourNow ? "now" : h.hour < hourNow ? "past" : ""}>
            <span className="uv-bar-val">{h.uv}</span>
            <span className="uv-bar" style={{ height: `${Math.max(4, (h.uv / max) * 100)}%`, ["--uv" as string]: c.color }} />
            <span className="uv-bar-hour">{h24 ? String(h.hour).padStart(2, "0") : `${h.hour % 12 || 12}${h.hour < 12 ? "a" : "p"}`}</span>
          </li>
        );
      })}
    </ol>
  );
}

export const uvWidget: WidgetDefinition<UvConfig> = {
  type: "uv",
  version: 1,
  name: "UV index",
  description: "Today’s hourly UV forecast from the EPA and National Weather Service, with WHO sun-protection advice.",
  icon: "umbrella",
  category: "weather",
  defaultSize: { w: 3, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 320,
  defaults: {},
  settings: [],
  provider: "epa-uv",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: Uv,
};
